/**
 * Unified product-tile hero image behaviour (Range PLP, Contentful tiles, upsell, blog).
 *
 * <= 2 images:
 *   Desktop (grid + carousel slide): hover swap — no inner Swiper
 *   Mobile grid:                     inner Swiper swipe + loop + progress bar, no arrows
 *   Mobile carousel slide:     inner Swiper + arrows + progress bar, no touch (outer carousel swipes; like InstockProductTile)
 *
 * > 2 images:
 *   Grid:            Swiper + loop + swipe; desktop arrows on hover; mobile swipe + progress bar (no arrows)
 *   Carousel slide:  Swiper + rewind, no touch; arrows always (mobile + desktop); progress bar
 *
 * staticHeroOnly: optional override to force first image only (no inner Swiper).
 *
 * 1 image: static hero everywhere.
 */

/** @typedef {'single' | 'hover-swap' | 'swipe' | 'carousel'} ProductTileImageMode */

export const PRODUCT_TILE_IMAGE_MODE = Object.freeze({
  SINGLE: 'single',
  HOVER_SWAP: 'hover-swap',
  SWIPE: 'swipe',
  CAROUSEL: 'carousel',
});

/**
 * @param {number} imageCount
 * @param {{ isMobile?: boolean, isInCarouselSlide?: boolean, staticHeroOnly?: boolean }} [opts]
 * @returns {ProductTileImageMode}
 */
export function getProductTileImageMode(
  imageCount,
  {isMobile = false, isInCarouselSlide = false, staticHeroOnly = false} = {}
) {
  if (staticHeroOnly && imageCount >= 1) {
    return PRODUCT_TILE_IMAGE_MODE.SINGLE;
  }

  if (!imageCount || imageCount <= 1) {
    return PRODUCT_TILE_IMAGE_MODE.SINGLE;
  }

  if (imageCount <= 2) {
    if (isInCarouselSlide && isMobile) {
      return PRODUCT_TILE_IMAGE_MODE.SWIPE;
    }

    if (isMobile) {
      return PRODUCT_TILE_IMAGE_MODE.SWIPE;
    }

    return PRODUCT_TILE_IMAGE_MODE.HOVER_SWAP;
  }

  return PRODUCT_TILE_IMAGE_MODE.CAROUSEL;
}

/**
 * CSS hook classes applied on the tile image wrapper (`swiper-range-tile` host).
 *
 * @param {ProductTileImageMode} mode
 * @returns {string}
 */
export function getProductTileImageBehaviorClassName(mode) {
  switch (mode) {
    case PRODUCT_TILE_IMAGE_MODE.HOVER_SWAP:
      return 'product-tile-image--hover-swap';
    case PRODUCT_TILE_IMAGE_MODE.SWIPE:
      return 'product-tile-image--swipe';
    case PRODUCT_TILE_IMAGE_MODE.CAROUSEL:
      return 'product-tile-image--carousel-multi';
    default:
      return '';
  }
}

/**
 * @param {ProductTileImageMode} mode
 * @returns {boolean}
 */
export function shouldUseProductTileSwiper(mode) {
  return (
    mode === PRODUCT_TILE_IMAGE_MODE.SWIPE ||
    mode === PRODUCT_TILE_IMAGE_MODE.CAROUSEL
  );
}

/**
 * Inner tile swiper loops whenever Swiper is active (grid + carousel, 2+ images).
 *
 * @returns {boolean}
 */
export function shouldEnableProductTileSwiperLoop() {
  return true;
}

/**
 * Carousel slide inner swiper is arrows-only — outer product-tiles carousel keeps horizontal swipe.
 *
 * @param {{ isInCarouselSlide?: boolean }} [opts]
 * @returns {boolean}
 */
export function shouldAllowProductTileSwiperTouch({isInCarouselSlide = false} = {}) {
  return !isInCarouselSlide;
}

/**
 * Grid / carousel slide 3+: desktop arrows on hover only; never on mobile.
 *
 * @param {ProductTileImageMode} mode
 * @param {{ isMobile?: boolean, isInCarouselSlide?: boolean }} [opts]
 * @returns {boolean}
 */
export function shouldShowProductTileSwiperNavigation(
  mode,
  {isMobile = false, isInCarouselSlide = false} = {}
) {
  if (!shouldUseProductTileSwiper(mode)) {
    return false;
  }

  if (isInCarouselSlide) {
    return true;
  }

  if (mode === PRODUCT_TILE_IMAGE_MODE.CAROUSEL) {
    return !isMobile;
  }

  return false;
}
